import React, { useState, useCallback } from "react"
import PropTypes from "prop-types"
import { Tag, Avatar, Button, Flex, Card } from "antd"
import { EllipsisOutlined } from "@ant-design/icons"
import { DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core"
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { restrictToParentElement } from "ui/SortableTableRow/restrictToParentElement"
import styles from "./index.module.css"
import IconWithBg from "ui/IconWithBg"
import CustomDrawer from "ui/Drawer"
import { EditIcon, TrashIcon } from "icons"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { routes } from "router/routes"
import { Text } from "ui/Typography"
import { DSIcon } from "ui/Icon/DSIcon"

const DragHandle = ({ dragHandleProps, dragDisabled }) => (
  <span
    className={dragDisabled ? `${styles.dragHandle} ${styles.dragHandleDisabled}` : styles.dragHandle}
    {...dragHandleProps}
    aria-label={dragDisabled ? "Reordering disabled while sorting or filtering" : "Drag to reorder"}
    aria-disabled={dragDisabled}
  >
    <DSIcon name="dragHandle" width={16} height={16} />
  </span>
)

DragHandle.propTypes = {
  dragHandleProps: PropTypes.object,
  dragDisabled: PropTypes.bool
}

const SortableCrewCard = ({ crew, onOpenDrawer, dragDisabled }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(crew.key ?? crew.id),
    disabled: dragDisabled
  })
  const navigate = useNavigate()

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1
  }

  return (
    <div ref={setNodeRef} style={style}>
      <Card
        className={styles.card}
        onClick={() => navigate(routes.guideCrewMemberDetailsPath({ id: crew.id }))}
        style={{ cursor: "pointer" }}
      >
        <Flex gap={10} align="center">
          <DragHandle dragHandleProps={dragDisabled ? null : { ...listeners, ...attributes }} dragDisabled={dragDisabled} />
          <div className="flex-1">
            <Flex align="flex-start" gap={16}>
              <Avatar src={crew.image} size={65} />
              <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                <Flex align="center" gap={8} wrap className={styles.nameRow}>
                  <span className={`${styles.name} fw-500`}>{crew.name}</span>
                  <Flex gap={8}>
                    {crew.status && (
                      <Tag color={crew.status === "active" ? "green" : "red"} className={styles.commonTag}>
                        {crew.status}
                      </Tag>
                    )}
                    {crew?.role && (
                      <Tag color="blue" className={styles.commonTag}>
                        {crew?.role}
                      </Tag>
                    )}
                  </Flex>
                </Flex>
                <Flex align="center" gap={8} className={styles.subRow}>
                  {crew?.title && (
                    <>
                      <Text type="secondary">{crew?.title}</Text>
                      <span className={styles.dot} />
                    </>
                  )}
                  <Text type="secondary">{crew.phoneNumber}</Text>
                </Flex>
              </Flex>
            </Flex>
            <Flex justify="space-between" className={`mt-s ${styles.detailsRow}`}>
              <Flex gap={5} className="w-full">
                <Text type="secondary" block className={styles.label}>Assigned to:</Text>
                <Text className={styles.value}>{crew.assignedTo} experiences</Text>
              </Flex>
              <Flex gap={5} className="w-full">
                <Text type="secondary" block className={styles.label}>Upcoming:</Text>
                <Text className={styles.value}>{crew.upcomingBookings} bookings</Text>
              </Flex>
            </Flex>
            <Flex justify="space-between" className={styles.detailsRow}>
              <Flex gap={5} className="w-full">
                <Text type="secondary" block className={styles.label}>Availability:</Text>
                <Text className={styles.value}>{crew.availability || "-"}</Text>
              </Flex>
              <Flex gap={5} className="w-full">
                <Text type="secondary" block className={styles.label}>Next Time Off:</Text>
                <Text className={styles.value}>{crew.nextTimeOff || "-"}</Text>
              </Flex>
            </Flex>
          </div>
          <IconWithBg
            onClick={(e) => { e.stopPropagation(); onOpenDrawer(crew) }}
            icon={<EllipsisOutlined />}
            className={styles.moreBtn}
          />
        </Flex>
      </Card>
    </div>
  )
}

SortableCrewCard.propTypes = {
  crew: PropTypes.object.isRequired,
  onOpenDrawer: PropTypes.func.isRequired,
  dragDisabled: PropTypes.bool
}

const CrewMobileCardList = ({ data, handleEdit, handleActivate, handleDeactivate, handleDelete, onReorder, dragDisabled }) => {
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false)
  const [selectedCrew, setSelectedCrew] = useState(null)
  const [liveIds, setLiveIds] = useState(null)
  const liveIdsRef = React.useRef(null)
  const lastOverIdRef = React.useRef(null)
  const { t } = useTranslation()

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  )

  const baseIds = data.map((crew) => String(crew.key ?? crew.id))
  const sortableIds = liveIds ?? baseIds

  const liveData = liveIds
    ? liveIds.map((id) => data.find((crew) => String(crew.key ?? crew.id) === id)).filter(Boolean)
    : data

  const openDrawerFilter = useCallback((crew) => {
    setOpenFilterDrawer(true)
    setSelectedCrew(crew)
  }, [])

  const handleDragOver = useCallback(
    ({ active, over }) => {
      if (!over || active.id === over.id) return
      if (lastOverIdRef.current === String(over.id)) return
      lastOverIdRef.current = String(over.id)
      const ids = liveIdsRef.current ?? baseIds
      const oldIndex = ids.indexOf(String(active.id))
      const newIndex = ids.indexOf(String(over.id))
      if (oldIndex === -1 || newIndex === -1) return
      const next = arrayMove(ids, oldIndex, newIndex)
      liveIdsRef.current = next
      setLiveIds(next)
    },
    [baseIds]
  )

  const handleDragEnd = useCallback(
    ({ active }) => {
      const finalIds = liveIdsRef.current
      liveIdsRef.current = null
      lastOverIdRef.current = null
      setLiveIds(null)
      if (!finalIds) return
      const activeId = String(active.id)
      const oldIndex = baseIds.indexOf(activeId)
      const newIndex = finalIds.indexOf(activeId)
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return
      const overId = baseIds[newIndex]
      if (!overId) return
      onReorder?.(activeId, overId)
    },
    [onReorder, baseIds]
  )

  const handleDragCancel = useCallback(() => {
    liveIdsRef.current = null
    lastOverIdRef.current = null
    setLiveIds(null)
  }, [])

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToParentElement]} onDragOver={handleDragOver} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        <Flex vertical gap={16}>
          {liveData.map((crew) => (
            <SortableCrewCard
              key={crew.key ?? crew.id}
              crew={crew}
              onOpenDrawer={openDrawerFilter}
              dragDisabled={dragDisabled}
            />
          ))}
        </Flex>
      </SortableContext>

      <CustomDrawer open={openFilterDrawer} title="Actions" setOpen={() => setOpenFilterDrawer(!openFilterDrawer)}>
        <Flex vertical gap={10} className="w-full">
          <Button color="primary" variant="outlined" size="large" onClick={() => { setOpenFilterDrawer(false); handleEdit(selectedCrew) }} className="w-full">
            <EditIcon /> {t("resources.edit") || "Edit"}
          </Button>
          {selectedCrew?.status === "active" ? (
            <Button color="primary" variant="outlined" size="large" onClick={() => { setOpenFilterDrawer(false); handleDeactivate(selectedCrew) }} className="w-full">
              <EditIcon /> {t("resources.deactivate") || "Deactivate"}
            </Button>
          ) : (
            <Button color="primary" variant="outlined" size="large" onClick={() => { setOpenFilterDrawer(false); handleActivate(selectedCrew) }} className="w-full">
              <EditIcon /> {t("resources.make_active") || "Make Active"}
            </Button>
          )}
          <Button color="danger" variant="outlined" size="large" onClick={() => { setOpenFilterDrawer(false); handleDelete(selectedCrew) }} className="w-full">
            <TrashIcon /> {t("resources.delete") || "Delete"}
          </Button>
        </Flex>
      </CustomDrawer>
    </DndContext>
  )
}

CrewMobileCardList.propTypes = {
  data: PropTypes.array.isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleActivate: PropTypes.func.isRequired,
  handleDeactivate: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  onReorder: PropTypes.func,
  dragDisabled: PropTypes.bool
}

export default CrewMobileCardList
